/**
 * useProjectTreeFilters Hook
 * 
 * Manages all filtering and sorting state for the ProjectTree component.
 * Provides a clean interface for filter operations and automatically syncs with user preferences.
 * 
 * Purpose: Consolidate filter/sort state management in one place
 * Usage: const { filters, sorting, actions } = useProjectTreeFilters();
 */

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { UserPreferences, YearWithProjects } from "@shared/schema";
import { filterProjects, hasActiveFilters, type FilterCriteria } from "../utils/filtering";
import { sortProjects, toggleSortColumn, type SortColumn, type SortDirection } from "../utils/sorting";

interface UseProjectTreeFiltersProps {
  userPreferences?: UserPreferences;
  years?: YearWithProjects[];
}

export function useProjectTreeFilters({ userPreferences, years }: UseProjectTreeFiltersProps) {
  // Filter state
  const [searchFilter, setSearchFilter] = useState("");
  const [clientFilter, setClientFilter] = useState("");
  const [leadArtistFilter, setLeadArtistFilter] = useState("");
  const [pinnedProjectsEnabled, setPinnedProjectsEnabled] = useState(false);
  const [pinnedProjects, setPinnedProjects] = useState<Array<{projectId: string, title: string}>>([]);
  const [hideFinals, setHideFinals] = useState(false);
  const [hideCanceled, setHideCanceled] = useState(true);
  const [hideWithEfforts, setHideWithEfforts] = useState(false);
  
  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  // Combo box states
  const [openClientCombo, setOpenClientCombo] = useState(false);
  const [openLeadArtistCombo, setOpenLeadArtistCombo] = useState(false);
  const [openPinnedCombo, setOpenPinnedCombo] = useState(false);
  const [showAddProjectToPinned, setShowAddProjectToPinned] = useState(false);
  
  // Track if preferences have been loaded
  const preferencesLoadedRef = useRef(false);
  
  // Load preferences on mount (once only)
  useEffect(() => {
    if (userPreferences && !preferencesLoadedRef.current) {
      preferencesLoadedRef.current = true;
      
      // Validate and set sort column
      if (userPreferences.sortColumn) {
        const validColumns: Array<'id' | 'project' | 'lead' | 'status' | 'createdAt'> = 
          ['id', 'project', 'lead', 'status', 'createdAt'];
        if (validColumns.includes(userPreferences.sortColumn as any)) {
          setSortColumn(userPreferences.sortColumn as SortColumn);
        }
      }
      if (userPreferences.sortDirection === 'asc' || userPreferences.sortDirection === 'desc') {
        setSortDirection(userPreferences.sortDirection);
      }
      if (userPreferences.searchFilter) {
        setSearchFilter(userPreferences.searchFilter);
      }
      if (userPreferences.clientFilter) {
        setClientFilter(userPreferences.clientFilter);
      }
      if (userPreferences.leadArtistFilter) {
        setLeadArtistFilter(userPreferences.leadArtistFilter);
      }
      if (userPreferences.pinnedProjectsEnabled !== undefined) {
        setPinnedProjectsEnabled(userPreferences.pinnedProjectsEnabled);
      }
      if (userPreferences.pinnedProjects) {
        setPinnedProjects(userPreferences.pinnedProjects as any);
      }
      if (userPreferences.hideFinals !== undefined) {
        setHideFinals(userPreferences.hideFinals);
      }
      setHideCanceled(userPreferences.hideCanceled ?? true);
      if (userPreferences.hideWithEfforts !== undefined) {
        setHideWithEfforts(userPreferences.hideWithEfforts);
      }
    }
  }, [userPreferences]);
  
  // Save preferences mutation
  const savePreferencesMutation = useMutation({
    mutationFn: async (prefs: Partial<UserPreferences>) => {
      return apiRequest('PATCH', '/api/user/preferences', prefs);
    }
  });
  
  // Auto-save preferences with debouncing
  useEffect(() => {
    if (!preferencesLoadedRef.current) return;
    
    const timeoutId = setTimeout(() => {
      savePreferencesMutation.mutate({
        sortColumn,
        sortDirection,
        searchFilter,
        clientFilter,
        leadArtistFilter,
        pinnedProjectsEnabled,
        pinnedProjects: pinnedProjects as any,
        hideFinals,
        hideCanceled,
        hideWithEfforts
      });
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [sortColumn, sortDirection, searchFilter, clientFilter, leadArtistFilter, pinnedProjectsEnabled, pinnedProjects, hideFinals, hideCanceled, hideWithEfforts]);
  
  // Column sort handler
  const handleColumnSort = useCallback((column: Exclude<SortColumn, null>) => {
    const newSort = toggleSortColumn(sortColumn, sortDirection, column);
    setSortColumn(newSort.column);
    setSortDirection(newSort.direction);
  }, [sortColumn, sortDirection]);
  
  // Clear all filters
  const resetSorting = useCallback(() => {
    setSortColumn(null);
    setSortDirection('asc');
  }, []);

  const clearFilters = useCallback(() => {
    setSearchFilter("");
    setClientFilter("");
    setLeadArtistFilter("");
    setPinnedProjectsEnabled(false);
    setHideFinals(false);
    setHideCanceled(true); // default: canceled hidden
    setHideWithEfforts(false);
  }, []);
  
  // Filtered and sorted years
  const filteredYears = useMemo(() => {
    if (!years) return [];
    
    const criteria: FilterCriteria = {
      searchFilter,
      clientFilter,
      leadArtistFilter,
      pinnedProjectsEnabled,
      pinnedProjects,
      hideFinals,
      hideCanceled,
      hideWithEfforts
    };
    
    return years.map(year => ({
      ...year,
      projects: sortProjects(
        filterProjects(year.projects || [], criteria),
        { column: sortColumn, direction: sortDirection }
      )
    }));
  }, [years, searchFilter, clientFilter, leadArtistFilter, sortColumn, sortDirection, pinnedProjectsEnabled, pinnedProjects, hideFinals, hideCanceled, hideWithEfforts]);
  
  return {
    // Filter state
    filters: {
      search: searchFilter,
      client: clientFilter,
      leadArtist: leadArtistFilter,
      pinnedEnabled: pinnedProjectsEnabled,
      pinnedProjects,
      hideFinals,
      hideCanceled,
      hideWithEfforts
    },
    // Sorting state
    sorting: {
      column: sortColumn,
      direction: sortDirection
    },
    // Combo box states
    comboBoxes: {
      clientOpen: openClientCombo,
      setClientOpen: setOpenClientCombo,
      leadArtistOpen: openLeadArtistCombo,
      setLeadArtistOpen: setOpenLeadArtistCombo,
      pinnedOpen: openPinnedCombo,
      setPinnedOpen: setOpenPinnedCombo,
      showAddToPinned: showAddProjectToPinned,
      setShowAddToPinned: setShowAddProjectToPinned
    },
    // Actions
    actions: {
      setSearchFilter,
      setClientFilter,
      setLeadArtistFilter,
      setPinnedProjectsEnabled,
      setPinnedProjects,
      setHideFinals,
      setHideCanceled,
      setHideWithEfforts,
      handleColumnSort,
      resetSorting,
      clearFilters
    },
    // Computed state
    filteredYears,
    hasActiveFilters: hasActiveFilters({
      searchFilter,
      clientFilter,
      leadArtistFilter,
      pinnedProjectsEnabled,
      pinnedProjects,
      hideFinals,
      hideCanceled,
      hideWithEfforts
    })
  };
}
